import * as utils from 'components/utility/utils';

/**
 * A utility class for manipulating model objects.
 */
export default class ModelOps {
	/**
	 * Gets a field by name.
	 */
	static hasField(model, name) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		return model.fields.hasOwnProperty(name);
	}

	/**
	 * Gets a field by name.
	 */
	static getField(model, name) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		if (model.fields.hasOwnProperty(name)) {
			return model.fields[name];
		}
		throw new Error(`model does not have field: ${name}`);
	}

	/**
	 * Gets the list of existing fields is the array of field's names (key),
	 * or if renamed, then the renamed field name.
	 */
	static getFieldNames(model) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		return Object.keys(model.fields);
	}

	/**
	 * Adds a field
	 */
	static addField(model, name, props) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		if (ModelOps.hasField(model, name)) {
			throw Error(`field already exists in model: ${name}`);
		}
		if (!props.hasOwnProperty('type')) {
			throw new TypeError('missing required property: type');
		}
		model.fields[name] = props;
		this.updateMetadata(model);
	}

	/**
	 * Deletes a field by name.
	 */
	static deleteField(model, name) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		if (!ModelOps.hasField(model, name)) {
			throw new Error(`model does not have field: ${name}`);
		}
		delete model.fields[name];
		this.updateMetadata(model);
	}

	/**
	 * Updates a field by name.
	 */
	static updateField(model, name, props) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		if (!ModelOps.hasField(model, name)) {
			throw Error(`model does not have field: ${name}`);
		}
		model.fields[name] = props;
		this.updateMetadata(model);
	}

	/**
	 * Renames a field.
	 */
	static renameField(model, name, rename) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}
		if (!ModelOps.hasField(model, name)) {
			throw Error(`model does not have field: ${name}`);
		}
		if (ModelOps.hasField(model, rename)) {
			throw Error(`field already exists in model: ${name}`);
		}
		// rename but preserve field order
		const newfields = {};
		Object.keys(model.fields).forEach(key => {
			if (key !== name) {
				newfields[key] = model.fields[key];
			} else {
				newfields[rename] = model.fields[key];
			}
		});
		model.fields = newfields;
		this.updateMetadata(model);
	}

	/**
	 * updates join metadata on model
	 */
	static updateMetadata(model) {
		if (typeof (model) !== 'object') {
			throw new TypeError('invalid model argument');
		}

		const joinData = this.getJoinData(model);
		if (!joinData) {
			delete model._joinmeta_;
			return;
		}

		if (joinData.error) {
			// eslint-disable-next-line no-console
			console.error(joinData.error, joinData.errorDetail);
		}	else if (!model.parent && joinData.parent) {
			model.parent = joinData.parent;
		} else if (model.parent !== joinData.parent) {
			// eslint-disable-next-line no-console
			console.error('Error: Model parent has changed');
		}

		model._joinmeta_ = joinData;
	}

	/**
	 * This function gets a bunch of info from the model regarding it's composite state, joins and
	 * parent information. Inherited fields must contain 'name' to avoid being considered for merge.
	 * Composite states:
	 * 1. Composite with join: model has fields which reference models. All of these models except
	 *    one parent are present in the join metadata, and join to a field of the parent.
	 *    <joinable AND parent AND hasJoin>
	 * 2. - UNSUPPORTED - Composite no join (merge): model has fields which reference more than one
	 *    model. All of these fields are interpreted as "array" type. No joins metadata is present,
	 *    or join metadata is present but more than one model referenced by a field does not exist
	 *    in the join metadata. (there should only be one parent)
	 *    <NOT joinable>
	 * 3. Composite Reduce: model has fields which only reference a single model. no joins exist.
	 *    This is very similar to composite with join, but no joins have taken place yet
	 *    <joinable AND parent>
	 * 4. - UNSUPPORTED - Vanilla Composite: model only has fields which don't reference any model.
	 *    <NOT parent OR NOT hasJoin>
	*/
	static getJoinData(model) {
		// and validation info is gathered.
		if (!model || !model.connector || model.connector.name !== 'composite') {
			return null;
		}
		const errors = [];
		const addError = error => {
			if (errors.indexOf(error) === -1) {
				errors.push(error);
			}
		};
		const joinedModels = [];
		const modelMergeTypes = {
		};
		const orphanModels = [];
		const relatedFields = {};
		let join = null;
		if (model.metadata) {
			// left join takes priority over inner join if it exists
			[ 'left_join', 'inner_join' ].forEach(type => {
				// save metadata for the join
				const data = model.metadata[type];
				// ignore if join already exists
				if (join && data) {
					addError('Model can only have one join type. left_join exists, so inner_join is not used');
					return;
				}
				if (!data) {
					return;
				}
				join = {
					type,
					data
				};
			});
		}
		// map joins to a dictionary with the model name as a key
		const modelMetadatas = {};
		if (join && Array.isArray(join.data)) {
			join.data.forEach(data => {
				if (!data.model) {
					return;
				}
				if (modelMetadatas[data.model]) {
					addError('Multiple joins to the same model exist. only the last will be used.');
				}
				modelMetadatas[data.model] = data;
			});
		} else if (join && join.data) {
			modelMetadatas[join.data.model] = join.data;
		}

		const processedModels = {};

		// loop over each field to get it's referenced model
		for (const fieldName in model.fields) {
			if (model.fields.hasOwnProperty(fieldName)) {
				const modelName = model.fields[fieldName].model;
				if (!modelName) {
					continue;
				}

				if (!relatedFields[modelName]) {
					relatedFields[modelName] = [];
				}
				relatedFields[modelName].push(fieldName);

				// TODO: find if model exists
				if (processedModels[modelName]) {
					continue;
				}

				processedModels[modelName] = true;
				if (modelMetadatas[modelName]) {
					joinedModels.push(modelName);
				} else {
					// orphan models are models not referenced in the joins metadata.
					// if one orphan exists then it will be used as the parent, and if
					// multiple exist, composite no join will take place
					orphanModels.unshift(modelName);
				}
			}
		}

		// TODO: add additional validation here or in seperate function
		// for example: compositeNoJoin fields which have a model should only be type: Array
		// composite join only works when there is one parent model
		let hasJoin = orphanModels.length === 1 && !!join;

		const parent = orphanModels.length === 1 ? orphanModels[0] : null;

		const getMergeType = modelName => {
			let mergeType = null;
			let field = null;
			(relatedFields[modelName] || []).forEach(name => {
				const type = utils.objectTypeToString(model.fields[name].type);
				let mt = null;
				switch (type) {
					case 'array':
					case 'object':
					// 'name' signifies the field comes from a model so the merge type is 'fields'
						if (model.fields[name].name) {
							mt = 'fields';
						} else {
							mt = type;
						}
						break;
					default:
						mt = 'fields';
				}
				if (mergeType && mergeType !== mt) {
					addError(`Fields (${field}, ${name}) making use of multiple merge types (${mergeType}, ${mt}) is not supported.`);
					return;
				}

				mergeType = mt;
				field = name;
			});
			return mergeType;
		};

		if (!parent) {
			addError('Composite model parent cannot be determined.');
		} else {
			const parentMergeType = getMergeType(parent);
			modelMergeTypes[parent] = parentMergeType;
		}
		const ignoredFields = [];
		if (!hasJoin && join) {
			// composite connector ignores any fields which are related to models with join data
			joinedModels.forEach(modelName => {
				if (relatedFields[modelName]) {
					ignoredFields.push(...relatedFields[modelName]);
				}
			});
		}

		const joins = [];
		if (hasJoin) {
			joinedModels.forEach(modelName => {
				const modelMeta = modelMetadatas[modelName];
				const props = modelMeta && modelMeta.join_properties;
				if (!props) {
					// no join_properties
					return;
				}
				modelMergeTypes[modelName] = getMergeType(modelName);
				// get the info for each individual join
				Object.keys(props).forEach(field => {
					if (!props.hasOwnProperty(field)) {
						return;
					}
					joins.push({
						/** the parent model */
						modelName: parent,
						/** the parent model's field to join on' */
						fieldName: props[field],
						/** second model that is joined to the first one */
						model: modelName,
						/** second model's field to join on */
						field,
						/** join type (inner_join/left_join). */
						type: join.type,
						/** merge type (object, array, fields) */
						merge: modelMergeTypes[modelName],
						/** fields related to the join */
						relatedFields: relatedFields[modelName]
					});
				});
			});

			if (!joins.length) {
				hasJoin = false;
			}
		}

		const joinable = (hasJoin || orphanModels.length <= 1) && !!parent;

		if (!joinable || errors.length) {
			// errors have been found, or the composite model isn't joinable. this isn't supported
			return { error: `Error: Unsupported model definition (${model.name})`, errorDetail: errors };
		}

		const modelData = {
			// does the model have (one or more) join(s) already?
			hasJoin,
			// map of models to the type of merge they are associated with
			modelMergeTypes,
			// is the model joinable?
			joinable,
			// what is the parent?
			parent
		};

		if (joinedModels.length) {
			// array of models that have been joined to the parent
			modelData.joinedModels = joinedModels;
		}
		if (ignoredFields.length) {
			// fields ignored by API Builder because of odd model definition
			modelData.ignoredFields = ignoredFields;
		}
		if (hasJoin) {
			modelData.type = join.type;
			modelData.joins = joins;
		}
		// return the errors some other time
		return modelData;
	}

	/**
	 * Remove a join and merge from the model def.
	 * The joinMeta is coming from joins list.
	 */
	static deleteJoinInfo(modelDef, joinMeta) {
		// Remove the fields from the join
		modelDef.fields = Object.keys(modelDef.fields).reduce(
			(prev, field) => {
				const fieldModel = modelDef.fields[field].model;
				if (fieldModel !== joinMeta.model) {
					prev[field] = modelDef.fields[field];
				}
				return prev;
			},
			{}
		);

		// Remove the join
		const metadata = modelDef.metadata = modelDef.metadata || {};
		const joins = metadata[joinMeta.type] || [];

		metadata[joinMeta.type] = joins.filter(j => (!(
			j.model === joinMeta.model
			&& j.join_properties
			&& j.join_properties[joinMeta.field]
			&& j.join_properties[joinMeta.field] === joinMeta.fieldName
		)));

		// Remove empty join metadata
		if (metadata[joinMeta.type] && metadata[joinMeta.type].length === 0) {
			delete metadata[joinMeta.type];
		}

		this.updateMetadata(modelDef);
	}

	/**
	 * Add the join info into the model definition.
	 */
	static addJoinInfo(modelDef, joinModel, joinData) {
		const { joinType, joinAs, joinAsFieldName, joinField, parentField } = joinData;

		// Merge the fields
		if (joinAs === 'fields') {
			// Merge all fields from join model
			modelDef.fields = Object.keys(joinModel.fields).reduce(
				(prev, field) => {
					let alias = field;
					if (prev[field]) {
						alias = `${field}_${joinModel.name}`;
					}

					prev[alias] = {
						...joinModel.fields[field],
						model: joinModel.name,
						name: field
					};

					return prev;
				},
				modelDef.fields
			);
		} else {
			// Add object/array field
			modelDef.fields[joinAsFieldName] = {
				model: joinModel.name,
				type: joinAs === 'object' ? 'object' : 'array'
			};
		}

		// Add the Join
		modelDef.metadata = modelDef.metadata || {};
		modelDef.metadata[joinType] = modelDef.metadata[joinType] || [];
		modelDef.metadata[joinType].push({
			model: joinModel.name,
			join_properties: {
				[joinField]: parentField
			}
		});

		this.updateMetadata(modelDef);
	}
}
